import { useCustomMutation, useNotification } from '@refinedev/core'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enGB } from 'date-fns/locale'
import { format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import ApplyDiscountModal from './ApplyDiscountModal'

interface AppliedDiscount {
  discount_id: number | null
  discount_code: string | null
  discount_description: string | null
  original_amount: number | null
  discount_amount: number | null
  final_amount: number | null
  applied_at: string | null
  expires_at: string | null
  expired: boolean | null
  handled: boolean | null
  handled_at: string | null
  handled_by: string | null
  payment_type: string | null
}

interface OrgAppliedDiscountPanelProps {
  orgUuid: string
  discount?: AppliedDiscount
  onChanged: () => void
}

function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined) return 'Not set'
  return `$${value.toFixed(2)}`
}

function formatDate(value: string | null | undefined) {
  if (!value) return 'Not set'
  return format(new Date(value), 'MMM d, yyyy')
}

export default function OrgAppliedDiscountPanel({
  orgUuid,
  discount,
  onChanged,
}: OrgAppliedDiscountPanelProps) {
  const { open: showNotification } = useNotification()
  const { mutate: updateExpiry, isLoading: isSaving } = useCustomMutation()
  const [editingExpiry, setEditingExpiry] = useState(false)
  const [expiryDate, setExpiryDate] = useState<Date | null>(null)
  const [inlineError, setInlineError] = useState<string | null>(null)
  const [applyOpen, setApplyOpen] = useState(false)

  const hasDiscount = !!discount?.discount_code
  const canEditExpiry =
    hasDiscount && discount?.expired === false && discount?.handled === false
  const stateLabel = !hasDiscount
    ? 'No applied discount'
    : discount?.expired
    ? discount?.handled
      ? 'Expired and handled'
      : 'Expired, unhandled'
    : discount?.handled
    ? 'Handled'
    : 'Active'

  const stateColor = useMemo(() => {
    if (!hasDiscount) return 'default'
    if (discount?.expired || discount?.handled) return 'warning'
    return 'success'
  }, [discount?.expired, discount?.handled, hasDiscount])

  useEffect(() => {
    setExpiryDate(discount?.expires_at ? new Date(discount.expires_at) : null)
    setInlineError(null)
    setEditingExpiry(false)
  }, [discount?.expires_at, discount?.discount_id])

  function handleSaveExpiry() {
    if (!expiryDate || isNaN(expiryDate.getTime())) {
      setInlineError('Choose a valid expiry date before saving.')
      return
    }

    setInlineError(null)
    updateExpiry(
      {
        url: `/discount-codes/orgs/${orgUuid}/discount/expires-at`,
        method: 'patch',
        values: { expires_at: expiryDate.toISOString() },
      },
      {
        onSuccess: () => {
          setEditingExpiry(false)
          showNotification?.({
            type: 'success',
            message: 'Discount expiry updated',
          })
          onChanged()
        },
        onError: (error: any) => {
          const detail =
            error?.response?.data?.detail ||
            error?.message ||
            'Unable to update the discount expiry.'
          setInlineError(detail)
          showNotification?.({
            type: 'error',
            message: 'Failed to update discount expiry',
            description: detail,
          })
        },
      },
    )
  }

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={1.5}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <LocalOfferIcon color="primary" fontSize="small" />
          <Typography variant="h6">Applied Discount</Typography>
          <Chip size="small" color={stateColor as any} label={stateLabel} />
        </Stack>
        {!hasDiscount && (
          <Button variant="contained" onClick={() => setApplyOpen(true)}>
            Apply Discount
          </Button>
        )}
      </Stack>

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: 2 }} />

      {inlineError && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 1,
          }}
        >
          <Typography color="error.main" variant="body2">
            {inlineError}
          </Typography>
        </Box>
      )}

      {!hasDiscount ? (
        <Box
          sx={{
            p: 1.5,
            border: '1px solid',
            borderColor: 'info.main',
            borderRadius: 1,
          }}
        >
          <Typography color="info.main" variant="body2">
            No discount is currently applied to this organisation.
          </Typography>
        </Box>
      ) : (
        <Stack gap={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Code
              </Typography>
              <Chip
                label={discount.discount_code}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Discount
              </Typography>
              <Typography fontWeight={600}>
                -{formatCurrency(discount.discount_amount)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Applied
              </Typography>
              <Typography fontWeight={600}>
                {formatDate(discount.applied_at)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Payment Type
              </Typography>
              <Typography fontWeight={600}>
                {discount.payment_type || 'Not set'}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Original Amount
              </Typography>
              <Typography>{formatCurrency(discount.original_amount)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Discounted Amount
              </Typography>
              <Typography>{formatCurrency(discount.final_amount)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Expiry
              </Typography>
              {!editingExpiry ? (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography fontWeight={600}>
                    {formatDate(discount.expires_at)}
                  </Typography>
                  {canEditExpiry && (
                    <Button
                      size="small"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => setEditingExpiry(true)}
                    >
                      Edit
                    </Button>
                  )}
                </Stack>
              ) : (
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={enGB}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                    <DatePicker
                      label="Expiry Date"
                      value={expiryDate}
                      onChange={(newValue) => setExpiryDate(newValue)}
                      format="dd/MM/yyyy"
                      disabled={isSaving}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSaveExpiry}
                      disabled={isSaving}
                    >
                      {isSaving ? <CircularProgress size={18} /> : 'Save'}
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingExpiry(false)
                        setExpiryDate(
                          discount.expires_at
                            ? new Date(discount.expires_at)
                            : null,
                        )
                        setInlineError(null)
                      }}
                      disabled={isSaving}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </LocalizationProvider>
              )}
            </Box>
          </Box>

          {!canEditExpiry && (
            <Box
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'info.main',
                borderRadius: 1,
              }}
            >
              <Typography color="info.main" variant="body2">
                Only active, unhandled discounts can have their expiry edited.
              </Typography>
            </Box>
          )}
        </Stack>
      )}

      {applyOpen && (
        <ApplyDiscountModal
          open={applyOpen}
          organisationUuid={orgUuid}
          onClose={() => setApplyOpen(false)}
          onSuccess={onChanged}
        />
      )}
    </Box>
  )
}
